/**
 * Doserne — Contract Generator PRO template builder.
 *
 * Pure functions that turn the admin's form data + the selected position
 * (fetched from /api/contract-settings/positions) into rich, WeasyPrint-
 * friendly HTML. Ported from the marketplace HoldingContractGenerator but
 * decoupled from all marketplace state/localStorage — it takes plain args
 * so it can live natively inside the Doserne portal.
 */

export const COMPANY_SETTINGS = {
  companyName: "Doserne Utleie AS",
  companyOrg: "937 207 050",
  companyAddress: "Brobekkveien 100, 0582 Oslo",
  warehouse: "Brobekkveien 100, 0582 Oslo; Hadelandsparken 8, 2742 Grua",
  otp: "2%",
};

export const CONTRACT_TRANSLATIONS = {
  sections: {
    employmentDetails: { en: "Employment details", no: "Ansettelsesdetaljer", ro: "Detalii angajare" },
    startDate: { en: "Start date", no: "Startdato", ro: "Data începerii" },
    contractDate: { en: "Contract date", no: "Kontraktdato", ro: "Data contractului" },
    contractType: { en: "Contract type", no: "Kontrakttype", ro: "Tip contract" },
    placeOfWork: { en: "Place of work", no: "Arbeidssted", ro: "Locul de muncă" },
    workingHours: { en: "Working hours", no: "Arbeidstid", ro: "Program de lucru" },
    normalWorkingTime: { en: "Normal working time", no: "Normal arbeidstid", ro: "Timp normal de lucru" },
    hoursPerWeek: { en: "37.5 hours per week (effective working time)", no: "37.5 timer per uke (effektiv arbeidstid)", ro: "37.5 ore pe săptămână (timp efectiv de lucru)" },
    dailyBreak: { en: "Daily break", no: "Daglig pause", ro: "Pauza zilnică" },
    breakMinutes: { en: "30 minutes unpaid break", no: "30 minutter ubetalt pause", ro: "30 minute pauză neplătită" },
    ordinaryHours: { en: "Ordinary hours", no: "Ordinære timer", ro: "Ore ordinare" },
    overtimeThreshold: { en: "Overtime threshold", no: "Overtidsgrense", ro: "Prag ore suplimentare" },
    overtimeAfter40: { en: "Overtime is work exceeding the statutory working hours of 40 hours per week", no: "Overtid er arbeid som overstiger den lovbestemte arbeidstiden på 40 timer per uke", ro: "Orele suplimentare sunt munca ce depășește durata legală de lucru de 40 de ore pe săptămână" },
    additionalHoursLabel: { en: "Additional hours (merarbeid)", no: "Merarbeid", ro: "Ore adiționale (merarbeid)" },
    additionalHoursValue: { en: "37.5–40 hours per week (work beyond agreed working time, but below the statutory limit)", no: "37.5–40 timer per uke (arbeid utover avtalt tid, under lovbestemt grense)", ro: "37,5–40 de ore pe săptămână (muncă peste timpul convenit, sub limita legală)" },
    overtimeLabel: { en: "Overtime", no: "Overtid", ro: "Ore suplimentare (overtid)" },
    overtimeValueStatutory: { en: "Work exceeding the statutory limits (normally 40 hours per 7 days or 9 hours per 24 hours)", no: "Arbeid utover lovbestemte grenser (normalt 40 timer per 7 dager eller 9 timer per 24 timer)", ro: "Muncă ce depășește limitele legale (în mod normal 40 de ore în 7 zile sau 9 ore în 24 de ore)" },
    saturdayLabel: { en: "Saturday", no: "Lørdag", ro: "Sâmbătă" },
    saturdayValue: { en: "Voluntary; ordinary working time/additional hours up to the statutory limits. Work exceeding the statutory limits is considered overtime.", no: "Frivillig; ordinær arbeidstid/merarbeid opp til lovbestemte grenser. Arbeid utover lovbestemte grenser regnes som overtid.", ro: "Voluntar; timp normal de lucru/ore adiționale până la limitele legale. Munca ce depășește limitele legale este considerată muncă suplimentară." },
    salary: { en: "Salary & payments", no: "Lønn og utbetalinger", ro: "Salariu și plăți" },
    hourlyRate: { en: "Hourly rate", no: "Timesats", ro: "Tarif orar" },
    paymentDate: { en: "Payment date", no: "Utbetalingsdato", ro: "Data plății" },
    overtime: { en: "Overtime", no: "Overtid", ro: "Ore suplimentare" },
    weekday: { en: "weekday", no: "hverdag", ro: "zi lucrătoare" },
    sunHolidays: { en: "Sun/holidays & 21:00–06:00", no: "Søn/helligdager & 21:00–06:00", ro: "Dum/sărbători & 21:00–06:00" },
    otpPension: { en: "OTP pension", no: "OTP pensjon", ro: "Pensie OTP" },
    bankAccount: { en: "Bank Account", no: "Bankkonto", ro: "Cont bancar" },
    equipment: { en: "Equipment & Tools", no: "Utstyr og verktøy", ro: "Echipament și unelte" },
    equipmentIntro: { en: "The company provides necessary work equipment and personal protective equipment (PPE).", no: "Selskapet tilbyr nødvendig arbeidsutstyr og personlig verneutstyr (PPE).", ro: "Compania furnizează echipamentul de lucru necesar și echipamentul de protecție personală (EIP)." },
    responsibility: { en: "Responsibility", no: "Ansvar", ro: "Responsabilitate" },
    responsibilityText: { en: "Each employee is responsible for the equipment they receive", no: "Hver ansatt er ansvarlig for utstyret de mottar", ro: "Fiecare angajat este responsabil de echipamentul pe care îl primește" },
    return: { en: "Return", no: "Retur", ro: "Returnare" },
    returnText: { en: "All equipment must be returned upon termination of employment", no: "Alt utstyr må returneres ved opphør av arbeidsforhold", ro: "Tot echipamentul trebuie returnat la încetarea contractului de muncă" },
    replacement: { en: "Replacement", no: "Erstatning", ro: "Înlocuire" },
    replacementText: { en: "Damaged equipment will be replaced - report any damage immediately", no: "Skadet utstyr vil bli erstattet - rapporter skader umiddelbart", ro: "Echipamentul deteriorat va fi înlocuit - raportați orice deteriorare imediat" },
    inspection: { en: "Inspection", no: "Inspeksjon", ro: "Inspecție" },
    inspectionText: { en: "Employees must inspect their tools and safety equipment daily before use and report any defects immediately.", no: "Ansatte må inspisere verktøyet og sikkerhetsutstyret sitt daglig før bruk og rapportere eventuelle feil umiddelbart.", ro: "Angajații trebuie să inspecteze zilnic uneltele și echipamentul de siguranță înainte de utilizare și să raporteze imediat orice defecte." },
    lossDamage: { en: "Loss/Damage", no: "Tap/Skade", ro: "Pierdere/Deteriorare" },
    lossDamageText: { en: "In cases of intentional misuse or gross negligence resulting in loss or damage of company equipment, the company may seek compensation in accordance with applicable Norwegian labour law.", no: "I tilfeller av forsettlig misbruk eller grov uaktsomhet som resulterer i tap eller skade på selskapets utstyr, kan selskapet kreve erstatning i samsvar med gjeldende norsk arbeidsrett.", ro: "În cazurile de utilizare abuzivă intenționată sau neglijență gravă care duce la pierderea sau deteriorarea echipamentului companiei, compania poate solicita despăgubiri în conformitate cu legislația norvegiană a muncii aplicabilă." },
    holiday: { en: "Holiday", no: "Ferie", ro: "Concediu" },
    holidayIntro: { en: "Holiday and holiday pay are regulated by", no: "Ferie og feriepenger er regulert av", ro: "Concediul și plata concediului sunt reglementate de" },
    holidayEntitlement: { en: "Holiday entitlement", no: "Ferierett", ro: "Drept la concediu" },
    weeksAndDay: { en: "4 weeks + 1 day", no: "4 uker + 1 dag", ro: "4 săptămâni + 1 zi" },
    holidayPay: { en: "Holiday pay", no: "Feriepenger", ro: "Plata concediului" },
    holidayPayPercent: { en: "10.2% (or higher if applicable)", no: "10,2% (eller høyere hvis aktuelt)", ro: "10,2% (sau mai mult dacă este cazul)" },
    holidayNotice: { en: "Important notice requirements:", no: "Viktige varselkrav:", ro: "Cerințe importante de notificare:" },
    holiday2Months: { en: "Holiday requests must be submitted at least 2 months in advance", no: "Ferieforespørsler må sendes inn minst 2 måneder i forveien", ro: "Cererile de concediu trebuie depuse cu cel puțin 2 luni în avans" },
    holidayRefuse: { en: "Management may adjust holiday periods based on operational needs in accordance with the Norwegian Holiday Act (Ferieloven).", no: "Ledelsen kan justere ferieperioder basert på driftsmessige behov i samsvar med ferieloven.", ro: "Conducerea poate ajusta perioadele de concediu în funcție de nevoile operaționale, în conformitate cu Legea norvegiană a concediilor (Ferieloven)." },
    holidayApproval: { en: "Approval is subject to operational needs and project schedules", no: "Godkjenning avhenger av driftsbehov og prosjektplaner", ro: "Aprobarea depinde de nevoile operaționale și programul proiectelor" },
    trialPeriod: { en: "Trial period", no: "Prøvetid", ro: "Perioadă de probă" },
    trialPeriodText: { en: "6 months (if applicable)", no: "6 måneder (hvis aktuelt)", ro: "6 luni (dacă este cazul)" },
    noticeDuringTrial: { en: "Notice during trial", no: "Oppsigelse under prøvetid", ro: "Preaviz în perioada de probă" },
    noticeDuringTrialText: { en: "14 days (mutual), unless otherwise required by law", no: "14 dager (gjensidig), med mindre annet kreves ved lov", ro: "14 zile (reciproc), cu excepția cazurilor prevăzute de lege" },
    termination: { en: "Termination", no: "Oppsigelse", ro: "Încetare contract" },
    first14Days: { en: "Special conditions during first 14 days:", no: "Spesielle vilkår de første 14 dagene:", ro: "Condiții speciale în primele 14 zile:" },
    first14DaysText: { en: "During the first 14 working days, both parties may terminate the employment with 1 day notice if something is not satisfactory.", no: "I løpet av de første 14 arbeidsdagene kan begge parter si opp arbeidsforholdet med 1 dags varsel hvis noe ikke er tilfredsstillende.", ro: "În primele 14 zile lucrătoare, ambele părți pot înceta contractul cu preaviz de 1 zi dacă ceva nu este satisfăcător." },
    standardNotice: { en: "Standard notice: 3 months mutual notice, calculated from the 1st of the month following the notice date.", no: "Standard oppsigelse: 3 måneders gjensidig oppsigelse, regnet fra den 1. i måneden etter oppsigelsesdatoen.", ro: "Preaviz standard: 3 luni preaviz reciproc, calculat de la data de 1 a lunii următoare datei notificării." },
    workingHoursFlexibility: { en: "Working hours may vary depending on project requirements.", no: "Arbeidstiden kan variere avhengig av prosjektkrav.", ro: "Programul de lucru poate varia în funcție de cerințele proiectului." },
  },
};

const PRINT_STYLES = `
  <style>
    @page { size: A4; margin: 16mm 14mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; font-size: 10.5pt; line-height: 1.45; color: #111827; margin: 0; }
    h1 { font-size: 20pt; text-align: center; margin: 0 0 18pt; letter-spacing: 1px; }
    table { border-collapse: collapse; width: 100%; margin-bottom: 10pt; }
    td { padding: 5pt 8pt; vertical-align: top; border: 1px solid #d1d5db; }
    .section-header { background: #23F7DD; color: #062e2b; padding: 6pt 10pt; font-weight: bold; font-size: 11.5pt; margin-top: 14pt; border-radius: 3pt 3pt 0 0; }
    .section-body { padding: 10pt; border: 1px solid #d1d5db; border-top: 0; margin-bottom: 12pt; }
    .label { color: #6b7280; font-weight: normal; }
    .career-path { padding: 8pt 10pt; margin-top: 8pt; border-radius: 4pt; }
    .career-path .pos-title { font-weight: bold; margin-top: 6pt; }
    .warning-box { background: #fffbeb; border-left: 4px solid #f59e0b; padding: 6pt 10pt; margin: 6pt 0; font-style: italic; }
    ul { margin: 4pt 0 4pt 16pt; padding: 0; }
    li { margin-bottom: 2pt; }
    .signature-block { margin-top: 26pt; }
    .sig-mark { height: 46pt; display: flex; align-items: flex-end; padding-bottom: 2pt; }
    .signature-line { border-top: 1px solid #111827; width: 220pt; margin-top: 40pt; padding-top: 4pt; }
    .lang-page { page-break-before: always; }
  </style>
`;

const isProgressionJob = (p) => !p.nonScaffoldProgression && (p.order || 0) > 0;

const renderRespItems = (items) =>
  (items || [])
    .map((r) => {
      if (/^\*\*.+\*\*$/.test(r)) {
        const clean = r.replace(/^\*\*|\*\*$/g, "");
        return `</ul><p style="font-weight:bold; margin-top:6pt; margin-bottom:2pt;">${clean}</p><ul>`;
      }
      return `<li>${r}</li>`;
    })
    .join("");

const formatDate = (iso, lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  const locale = lang === "no" ? "no-NO" : lang === "ro" ? "ro-RO" : "en-GB";
  return d.toLocaleDateString(locale, { day: "2-digit", month: "long", year: "numeric" });
};

/** Build the contract body (no <head>) for one language. */
function buildBody({ contractData, pos, positions, showCumulative, selectedLowerResp, settings, lang, employerSignature }) {
  const tL = (key) => CONTRACT_TRANSLATIONS.sections[key]?.[lang] || CONTRACT_TRANSLATIONS.sections[key]?.en || key;
  const isOT = !pos.noOvertime;

  const titleText = lang === "no" ? "ARBEIDSAVTALE" : lang === "ro" ? "CONTRACT DE MUNCĂ" : "EMPLOYMENT CONTRACT";
  const sec01Title = lang === "no" ? "Partene" : lang === "ro" ? "Părțile" : "Parties";
  const sec03Title = lang === "en" ? "Position & responsibilities" : lang === "no" ? "Stilling og ansvar" : "Poziție și responsabilități";
  const validUntilLabel = lang === "en" ? "Valid until" : lang === "no" ? "Gyldig til" : "Valabil până la";
  const validUntilWarn =
    lang === "en"
      ? "If the employee is not notified at least 14 days before the expiry date about a new contract being prepared, this contract is considered NOT renewed and employment ends automatically on the date above."
      : lang === "no"
        ? "Dersom den ansatte ikke er varslet minst 14 dager før utløpsdatoen om at en ny kontrakt vil bli utstedt, anses denne kontrakten som IKKE fornyet, og arbeidsforholdet opphører automatisk på datoen over."
        : "Dacă angajatul nu este informat cu cel puțin 14 zile înainte de data de expirare despre încheierea unui contract nou, acest contract este considerat NEREÎNNOIT, iar raportul de muncă încetează automat la data de mai sus.";

  const respList = `<ul>${renderRespItems(pos.responsibilities?.[lang] || pos.responsibilities?.en || [])}</ul>`;
  const limList = (pos.limitations?.[lang] || pos.limitations?.en || []).map((l) => `<li>${l}</li>`).join("");
  const customTasks = (contractData.customTasks || []).map((tk) => `<li><i>${tk}</i></li>`).join("");

  // Employer signature — auto-stamped from the admin's saved /doserne signature.
  const es = employerSignature || {};
  let empSigMark = "";
  if (es.type === "drawn" && es.url) {
    empSigMark = `<img src="${es.url}" style="max-height:44pt; max-width:210pt;" />`;
  } else if (es.type === "typed" && es.text) {
    empSigMark = `<span style="font-family:'Segoe Script','Brush Script MT','Comic Sans MS',cursive; font-size:22pt; line-height:1;">${es.text}</span>`;
  }

  // Career path (cumulative) — future levels + inherited selected lower-level tasks.
  let careerPathHtml = "";
  if (showCumulative) {
    const currentOrder = pos.order || 0;
    const higher = (positions || [])
      .filter((p) => isProgressionJob(p) && (p.order || 0) > currentOrder && p.id !== pos.id)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const lower = (positions || [])
      .filter((p) => isProgressionJob(p) && (p.order || 0) < currentOrder && p.id !== pos.id && p.responsibilities)
      .sort((a, b) => (a.order || 0) - (b.order || 0));
    const parts = [];
    if (higher.length) {
      const cpTitle = lang === "en" ? "Career growth path — future responsibilities" : lang === "no" ? "Karrierevei — fremtidige ansvar" : "Cale de creștere — responsabilități viitoare";
      const blocks = higher
        .map((hp) => `<div class="pos-title">${hp.label}</div><ul>${renderRespItems(hp.responsibilities?.[lang] || hp.responsibilities?.en || [])}</ul>`)
        .join("");
      parts.push(`<div class="career-path" style="background:#eff6ff; border-left:4px solid #60a5fa;"><p><b>${cpTitle}</b></p>${blocks}</div>`);
    }
    if (lower.length) {
      const cpTitle = lang === "en" ? "Includes selected responsibilities from lower levels" : lang === "no" ? "Inkluderer valgte ansvar fra lavere nivåer" : "Include responsabilitățile selectate de la nivelurile inferioare";
      const blocks = lower
        .map((hp) => {
          const list = (hp.responsibilities?.[lang] || hp.responsibilities?.en || []).filter((_, i) => selectedLowerResp[`${hp.id}:${i}`] !== false);
          if (!list.length) return "";
          return `<div class="pos-title">${hp.label}</div><ul>${renderRespItems(list)}</ul>`;
        })
        .filter(Boolean)
        .join("");
      if (blocks) parts.push(`<div class="career-path" style="background:#ecfdf5; border-left:4px solid #10b981;"><p><b>${cpTitle}</b></p>${blocks}</div>`);
    }
    careerPathHtml = parts.join("");
  }

  return `
    <h1>${titleText}</h1>

    <div class="section-header">01 — ${sec01Title}</div>
    <div class="section-body">
      <table>
        <tr>
          <td style="width:50%">
            <b>${settings.companyName}</b> (Org.nr: ${settings.companyOrg})<br>
            ${settings.companyAddress}<br><br>
            <b>${lang === "no" ? "Lager" : lang === "ro" ? "Depozit" : "Warehouse"}:</b> ${settings.warehouse}
          </td>
          <td style="width:50%">
            <span class="label">${lang === "no" ? "Navn" : lang === "ro" ? "Nume" : "Name"}:</span> ${contractData.empName || "—"}<br>
            <span class="label">${lang === "no" ? "Person ID" : lang === "ro" ? "CNP" : "Personal ID"}:</span> ${contractData.empId || "—"}<br>
            <span class="label">${lang === "no" ? "Adresse" : lang === "ro" ? "Adresa" : "Address"}:</span> ${contractData.empAddress || "—"}<br>
            <span class="label">${lang === "no" ? "Telefon" : lang === "ro" ? "Telefon" : "Phone"}:</span> ${contractData.empPhone || "—"}<br>
            <span class="label">E-post:</span> ${contractData.empEmail || "—"}
          </td>
        </tr>
      </table>
    </div>

    <div class="section-header">02 — ${tL("employmentDetails")}</div>
    <div class="section-body">
      <table>
        <tr><td class="label" style="width:32%">${tL("startDate")}</td><td>${formatDate(contractData.startDate, lang) || "—"}</td></tr>
        <tr><td class="label">${tL("contractDate")}</td><td>${formatDate(contractData.contractDate, lang)}</td></tr>
        <tr><td class="label">${tL("contractType")}</td><td>${contractData.contractType}</td></tr>
        ${contractData.contractType !== "Permanent" && contractData.endDate ? `<tr><td class="label"><b>${validUntilLabel}</b></td><td><b>${formatDate(contractData.endDate, lang)}</b></td></tr>` : ""}
        <tr><td class="label">${tL("placeOfWork")}</td><td>${contractData.placeOfWork}</td></tr>
      </table>
      ${contractData.contractType !== "Permanent" && contractData.endDate ? `<div class="warning-box">⚠️ ${validUntilWarn}</div>` : ""}
    </div>

    <div class="section-header">03 — ${sec03Title}</div>
    <div class="section-body">
      <p><b>${lang === "no" ? "Stilling" : lang === "ro" ? "Poziție" : "Position"}:</b> ${pos.label}</p>
      <p class="label" style="margin-top:8pt;">${lang === "en" ? "Role expectations" : lang === "no" ? "Rolleforventninger" : "Așteptări de rol"}</p>
      ${respList}
      ${customTasks ? `<ul>${customTasks}</ul>` : ""}
      ${careerPathHtml}
      <p class="label" style="margin-top:8pt;">${lang === "en" ? "Limitations / authority" : lang === "no" ? "Begrensninger / myndighet" : "Limitări / autoritate"}</p>
      <ul>${limList}</ul>
    </div>

    <div class="section-header">04 — ${tL("workingHours")}</div>
    <div class="section-body">
      <table>
        <tr><td class="label" style="width:32%">${tL("normalWorkingTime")}</td><td>${tL("hoursPerWeek")}</td></tr>
        <tr><td class="label">${tL("dailyBreak")}</td><td>${tL("breakMinutes")}</td></tr>
        <tr><td class="label">${tL("ordinaryHours")}</td><td>${contractData.workHours}</td></tr>
        ${isOT ? `<tr><td class="label">${tL("additionalHoursLabel")}</td><td>${tL("additionalHoursValue")}</td></tr>
        <tr><td class="label">${tL("overtimeLabel")}</td><td>${tL("overtimeValueStatutory")}</td></tr>
        <tr><td class="label">${tL("saturdayLabel")}</td><td>${tL("saturdayValue")}</td></tr>` : ""}
      </table>
      <p style="font-style:italic; margin-top:6pt;">${tL("workingHoursFlexibility")}</p>
    </div>

    <div class="section-header">05 — ${tL("salary")}</div>
    <div class="section-body">
      <table>
        <tr><td class="label" style="width:32%">${tL("hourlyRate")}</td><td>${contractData.hourlyRate} NOK/hour</td></tr>
        <tr><td class="label">${tL("paymentDate")}</td><td>${contractData.payDate}</td></tr>
        ${isOT ? `<tr><td class="label">${tL("overtime")}</td><td>${contractData.otWeekday} (${tL("weekday")}) • ${contractData.otSpecial} (${tL("sunHolidays")})</td></tr>` : ""}
        <tr><td class="label">${tL("otpPension")}</td><td>${settings.otp}</td></tr>
        ${contractData.empBankAccount ? `<tr><td class="label">${tL("bankAccount")}</td><td>${contractData.empBankAccount}</td></tr>` : ""}
      </table>
    </div>

    <div class="section-header">06 — ${tL("equipment")}</div>
    <div class="section-body">
      <p>${tL("equipmentIntro")}</p>
      <ul>
        <li><b>${tL("responsibility")}:</b> ${tL("responsibilityText")}</li>
        <li><b>${tL("return")}:</b> ${tL("returnText")}</li>
        <li><b>${tL("replacement")}:</b> ${tL("replacementText")}</li>
        <li><b>${tL("inspection")}:</b> ${tL("inspectionText")}</li>
        <li><b>${tL("lossDamage")}:</b> ${tL("lossDamageText")}</li>
      </ul>
    </div>

    <div class="section-header">07 — ${tL("holiday")}</div>
    <div class="section-body">
      <p>${tL("holidayIntro")} <b>Ferieloven</b>.</p>
      <p><b>${tL("holidayEntitlement")}:</b> ${tL("weeksAndDay")}. <b>${tL("holidayPay")}:</b> ${tL("holidayPayPercent")}.</p>
      <div class="warning-box">
        <b>${tL("holidayNotice")}</b>
        <ul style="margin-top:4pt;">
          <li>${tL("holiday2Months")}</li>
          <li>${tL("holidayRefuse")}</li>
          <li>${tL("holidayApproval")}</li>
        </ul>
      </div>
    </div>

    <div class="section-header">08 — ${tL("trialPeriod")}</div>
    <div class="section-body">
      <p><b>${tL("trialPeriod")}:</b> ${tL("trialPeriodText")}</p>
      <p><b>${tL("noticeDuringTrial")}:</b> ${tL("noticeDuringTrialText")}</p>
    </div>

    <div class="section-header">09 — ${tL("termination")}</div>
    <div class="section-body">
      <p><b>${tL("first14Days")}</b> — ${tL("first14DaysText")}</p>
      <p style="margin-top:6pt;">${tL("standardNotice")}</p>
    </div>

    <div class="signature-block">
      <table>
        <tr>
          <td style="width:50%; border:0; vertical-align:bottom;">
            <div class="sig-mark">${empSigMark}</div>
            <div class="signature-line" style="margin-top:0;"></div>
            <p><b>${lang === "no" ? "Arbeidsgiver" : lang === "ro" ? "Angajator" : "Employer"}</b></p>
            <p>${settings.companyName}</p>
          </td>
          <td style="width:50%; border:0; vertical-align:bottom;">
            <div class="sig-mark" data-employee-sig="1"></div>
            <div class="signature-line" style="margin-top:0;"></div>
            <p><b>${lang === "no" ? "Arbeidstaker" : lang === "ro" ? "Angajat" : "Employee"}</b></p>
            <p>${contractData.empName || ""}</p>
          </td>
        </tr>
      </table>
    </div>

    ${contractData.notes ? `<p style="margin-top:18pt;"><b>${lang === "no" ? "Merknader" : lang === "ro" ? "Mențiuni" : "Notes"}:</b> ${contractData.notes}</p>` : ""}
  `;
}

/**
 * Build the full HTML document. `langs` is an array like ["no","ro","en"];
 * each selected language becomes its own page (page-break) inside one PDF.
 */
export function buildContractHtml({ contractData, pos, positions, showCumulative = false, selectedLowerResp = {}, langs = ["no"], settings = COMPANY_SETTINGS, employerSignature = null }) {
  const selected = (langs && langs.length ? langs : ["no"]).filter((l) => ["no", "ro", "en"].includes(l));
  const bodies = selected
    .map((lang, i) => {
      const body = buildBody({ contractData, pos, positions, showCumulative, selectedLowerResp, settings, lang, employerSignature });
      return `<div class="${i === 0 ? "" : "lang-page"}">${body}</div>`;
    })
    .join("\n");

  return `<!DOCTYPE html>
<html lang="${selected[0] || "no"}">
<head>
  <meta charset="utf-8">
  <title>${settings.companyName} — Contract</title>
  ${PRINT_STYLES}
</head>
<body>
${bodies}
</body>
</html>`;
}
